import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { CampaignItem } from '../types';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import {
  Layers,
  Plus,
  Link as LinkIcon,
  MousePointerClick,
  Edit2,
  Trash2,
  ExternalLink,
  Calendar,
  Sparkles,
} from 'lucide-react';
import { formatDate, formatNumber } from '../lib/utils';
import { useToast } from '../hooks/useToast';

interface CampaignsListProps {
  onNavigate: (path: string) => void;
  onSelectCampaign: (id: string) => void;
}

export const CampaignsList: React.FC<CampaignsListProps> = ({ onNavigate, onSelectCampaign }) => {
  const { showToast } = useToast();
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create / Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignItem | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Delete modal state
  const [deleteTarget, setDeleteTarget] = useState<CampaignItem | null>(null);

  const fetchCampaigns = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{ items: CampaignItem[] }>('/campaigns');
      setCampaigns(data.items || []);
    } catch (err: any) {
      showToast('Failed to load campaigns', err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const openCreateModal = () => {
    setEditingCampaign(null);
    setName('');
    setDescription('');
    setUtmSource('');
    setUtmMedium('');
    setUtmCampaign('');
    setIsModalOpen(true);
  };

  const openEditModal = (c: CampaignItem) => {
    setEditingCampaign(c);
    setName(c.name);
    setDescription(c.description || '');
    setUtmSource(c.utm_source || '');
    setUtmMedium(c.utm_medium || '');
    setUtmCampaign(c.utm_campaign || '');
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast('Name Required', 'Please enter a campaign name', 'error');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        utm_source: utmSource.trim() || null,
        utm_medium: utmMedium.trim() || null,
        utm_campaign: utmCampaign.trim() || null,
      };

      if (editingCampaign) {
        await api.put(`/campaigns/${editingCampaign.id}`, payload);
        showToast('Campaign Updated', `"${name}" has been updated`, 'success');
      } else {
        await api.post('/campaigns', payload);
        showToast('Campaign Created', `"${name}" is ready`, 'success');
      }

      setIsModalOpen(false);
      fetchCampaigns();
    } catch (err: any) {
      showToast('Save Failed', err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/campaigns/${deleteTarget.id}`);
      showToast('Campaign Deleted', `"${deleteTarget.name}" removed and associated links unlinked`, 'info');
      setDeleteTarget(null);
      fetchCampaigns();
    } catch (err: any) {
      showToast('Delete Failed', err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">
            Marketing Campaigns
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Organize multi-channel links under targeted marketing campaigns for unified performance tracking.
          </p>
        </div>

        <Button
          variant="primary"
          onClick={openCreateModal}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Create Campaign
        </Button>
      </div>

      {/* Campaign Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full py-16 text-center text-slate-500">
            Loading campaigns...
          </div>
        ) : campaigns.length > 0 ? (
          campaigns.map((camp) => (
            <Card key={camp.id} hoverEffect className="flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="p-2 rounded-xl bg-orange-500/10 text-orange-400 border border-orange-500/20">
                    <Layers className="w-5 h-5" />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditModal(camp)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                      title="Edit campaign"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(camp)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors cursor-pointer"
                      title="Delete campaign"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <h3
                  onClick={() => onSelectCampaign(camp.id)}
                  className="text-base font-bold text-slate-100 hover:text-orange-400 transition-colors cursor-pointer tracking-tight"
                >
                  {camp.name}
                </h3>
                {camp.description && (
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                    {camp.description}
                  </p>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-slate-800/80">
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div className="p-2 rounded-lg bg-slate-950/60 border border-slate-800">
                    <span className="text-[10px] text-slate-500 uppercase font-semibold block">Links</span>
                    <span className="font-mono font-bold text-slate-200">
                      {camp.total_links || 0} active
                    </span>
                  </div>
                  <div className="p-2 rounded-lg bg-slate-950/60 border border-slate-800">
                    <span className="text-[10px] text-slate-500 uppercase font-semibold block">Clicks</span>
                    <span className="font-mono font-bold text-orange-400">
                      {formatNumber(camp.total_clicks)}
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => onSelectCampaign(camp.id)}
                >
                  View Campaign Links & Stats
                </Button>
              </div>
            </Card>
          ))
        ) : (
          <div className="col-span-full py-12 text-center text-slate-500">
            No campaigns created yet. Click "Create Campaign" to organize your marketing links.
          </div>
        )}
      </div>

      {/* Create / Edit Campaign Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingCampaign ? 'Edit Campaign' : 'Create Marketing Campaign'}
        description="Configure default campaign tags and grouping"
        maxWidth="md"
      >
        <form onSubmit={handleSave} className="space-y-4">
          <Input
            label="Campaign Name"
            required
            placeholder="e.g. AI Builder Challenge 2026"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
              Description (Optional)
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. October 2026 marketing drive for students across Kerala"
              className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-100 placeholder:text-slate-500 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80"
            />
          </div>

          <div className="pt-2 border-t border-slate-800 space-y-3">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">
              Default UTM Preset Tags
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="utm_source"
                placeholder="e.g. brototype"
                value={utmSource}
                onChange={(e) => setUtmSource(e.target.value)}
              />
              <Input
                label="utm_medium"
                placeholder="e.g. social"
                value={utmMedium}
                onChange={(e) => setUtmMedium(e.target.value)}
              />
            </div>
            <Input
              label="utm_campaign"
              placeholder="e.g. ai-builder-challenge"
              value={utmCampaign}
              onChange={(e) => setUtmCampaign(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-800">
            <Button type="button" variant="ghost" size="sm" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" isLoading={isSaving}>
              {editingCampaign ? 'Save Changes' : 'Create Campaign'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <Modal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title="Delete Campaign"
          description={`Campaign: ${deleteTarget.name}`}
          maxWidth="sm"
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-300 leading-relaxed">
              Deleting this campaign will not delete its short links. The links will simply be unlinked and remain active.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete}>
                Confirm Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
